import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package, BarChart3, FileText, Clock, MessageSquare, ShoppingCart, Zap, Users, Calendar, CalendarDays, DollarSign, Download, Check, X, MapPin, Wrench, Receipt, HelpCircle, ListChecks, Smartphone, Building2, Megaphone, Gift, Folder } from 'lucide-react';
import AppUpdateModal, { AppUpdatePost } from '../components/AppUpdateModal';
import { APP_UPDATES, formatAppUpdateDate, type AppUpdate } from '../lib/appUpdates';
import { Integrations } from './settings/Integrations';
import { useAppSystem } from '../hooks/useAppSystem';
import { useBusiness } from '../contexts/BusinessContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// Custom PNG app icons (keyed by slug)
const customAppIcons: { [key: string]: string } = {
  'analytics': '/Analyticsappicon.png',
  'projects': '/Projectsappicon.png',
  'calendar': '/Calenderappicon.png',
  'crm': '/CRMappicon.png',
  'proposals': '/Proposalsappicon.png',
  'tasks': '/Bookingsappicon.png',
  'inventory': '/Inventoryappicon.png',
  'expenses': '/Expensesappicon.png',
  'payroll': '/Timesheetsappicon.png',
  'teams': '/Teamsappicon.png',
  'invoices': '/Invoicesappicon.png',
  'automations': '/Automateappicon.png',
  'services': '/Servicesappicon.png',
  'customers': '/Customersappicon.png',
  'marketing': '/Marketingappicon.png',
  'gift-cards': '/Giftcardsappicon.png',
  'messages': '/Messagesappicon.png',
  'bookings': '/Bookingsappicon.png',
  'products': '/Storeappicon.png',
  'mobile-calendar': '/Fieldproappicon.png',
  'm-calendar': '/Fieldproappicon.png',
};

// Icon helper - matches sidebar icons from LeftNavigationV2
const getIconComponent = (appSlug: string, size: string = "h-8 w-8") => {
  const iconMap: { [key: string]: React.ReactNode } = {
    'calendar': <Calendar className={size} />,
    'mobile-calendar': <Smartphone className={size} />,
    'm-calendar': <Smartphone className={size} />,
    'customers': <Users className={size} />,
    'automations': <Zap className={size} />,
    'teams': <Users className={size} />,
    'tasks': <ListChecks className={size} />,
    'analytics': <BarChart3 className={size} />,
    'crm': <Building2 className={size} />,
    'inventory': <Package className={size} />,
    'payroll': <DollarSign className={size} />,
    'services': <Wrench className={size} />,
    'bookings': <CalendarDays className={size} />,
    'proposals': <FileText className={size} />,
    'marketing': <Megaphone className={size} />,
    'invoices': <Receipt className={size} />,
    'messages': <MessageSquare className={size} />,
    'products': <ShoppingCart className={size} />,
    'gift-cards': <Gift className={size} />,
    'projects': <Folder className={size} />,
    'que': <ListChecks className={size} />,
  };
  
  return iconMap[appSlug] || <Package className={size} />;
};

interface App {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  price: number | 'free';
  rating: number;
  reviews: number;
  icon: JSX.Element;
  color: string;
  features: string[];
  screenshots?: string[];
  popular?: boolean;
  new?: boolean;
  type?: 'core' | 'addon';
  slug?: string;
  uninstallable?: boolean;
  isSystemPage?: boolean; // Special flag for system pages that navigate instead of installing
}

const coreApps: App[] = [
  {
    id: 'teams',
    name: 'Teams',
    tagline: 'Manage your team members and resources',
    description: 'Complete team management solution with resource scheduling, permissions, and time tracking capabilities.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('teams', 'h-8 w-8'),
    color: 'bg-blue-600',
    type: 'core',
    slug: 'teams',
    uninstallable: false,
    features: [
      'Team member management',
      'Resource scheduling',
      'Permission roles',
      'Time off requests',
      'Time clock system',
      'Mobile availability'
    ]
  },
  {
    id: 'calendar',
    name: 'Calendar',
    tagline: 'Schedule and manage appointments',
    description: 'Advanced calendar system for booking management, scheduling, and appointment tracking.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('calendar', 'h-8 w-8'),
    color: 'bg-green-600',
    type: 'core',
    slug: 'calendar',
    uninstallable: true,
    features: [
      'Appointment scheduling',
      'Resource allocation',
      'Booking management',
      'Calendar views',
      'Mobile calendar',
      'Availability tracking'
    ]
  },
  // Field-Pro (mobile-calendar) is retired: hidden from the store and excluded
  // from all plans (migration 20260710030000). Kept in the DB registry so
  // existing installs keep working.
  {
    id: 'services',
    name: 'Services',
    tagline: 'Service catalog and pricing management',
    description: 'Manage your service offerings, pricing, and workflows with advanced service configuration.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: <Wrench className="h-8 w-8" />,
    color: 'bg-gray-600',
    type: 'core',
    slug: 'services',
    uninstallable: false,
    features: [
      'Service catalog',
      'Pricing management',
      'Workflow automation',
      'Service categories',
      'Duration tracking',
      'Resource requirements'
    ]
  },
  {
    id: 'customers',
    name: 'Customers',
    tagline: 'Customer relationship management',
    description: 'Complete CRM solution for managing customer relationships, history, and communications.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('customers', 'h-8 w-8'),
    color: 'bg-indigo-600',
    type: 'core',
    slug: 'customers',
    uninstallable: false,
    features: [
      'Customer profiles',
      'Contact management',
      'Booking history',
      'Communication tracking',
      'Address management',
      'Customer insights'
    ]
  },
  {
    id: 'automations',
    name: 'Automate',
    tagline: 'Workflow automation and triggers',
    description: 'Create automated workflows to streamline your business processes with triggers, actions, and conditional logic.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('automations', 'h-8 w-8'),
    color: 'bg-yellow-600',
    type: 'core',
    slug: 'automations',
    uninstallable: false,
    features: [
      'Visual workflow builder',
      'Email & SMS automation',
      'Custom triggers',
      'Conditional logic',
      'Booking reminders',
      'Follow-up sequences'
    ]
  },
  {
    id: 'invoices',
    name: 'Invoices',
    tagline: 'Professional invoice and payment management',
    description: 'Manage all your invoices, track payments, handle both booking and product sale invoices with comprehensive payment processing.',
    category: 'Core Apps',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: <Receipt className="h-8 w-8" />,
    color: 'bg-blue-600',
    type: 'core',
    slug: 'invoices',
    uninstallable: false,
    features: [
      'Invoice management',
      'Payment tracking',
      'Booking invoices',
      'Product sale invoices',
      'Payment processing',
      'Financial reporting'
    ]
  },
];

const addonApps: App[] = [
  {
    id: 'tasks',
    name: 'Docs',
    tagline: 'SOPs, safety checklists & forms your team can complete',
    description: 'Build customizable documents — SOPs, safety checklists, daily duties, and signable forms — assign them to your team, and collect completions.',
    category: 'Productivity',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('tasks', 'h-8 w-8'),
    color: 'bg-blue-600',
    type: 'addon',
    slug: 'tasks',
    uninstallable: true,
    features: [
      'Build custom documents & checklists',
      'SOPs, safety forms & daily duties',
      'AI create from a prompt or PDF',
      'Assign to roles or specific people',
      'Signatures & required fields',
      'Categories, tags & preview'
    ]
  },
  // Que is discontinued: hidden from the store (migration 20260710040000
  // marks its add-on unavailable). Existing installs keep working.
  {
    id: 'analytics',
    name: 'Analytics',
    tagline: 'Advanced business analytics and KPI dashboards',
    description: 'Create custom KPI dashboards with real-time analytics, performance metrics, and business intelligence insights to track your success.',
    category: 'Analytics',
    price: 'free',
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('analytics', 'h-8 w-8'),
    color: 'bg-green-600',
    type: 'addon',
    slug: 'analytics',
    uninstallable: true,
    features: [
      'Custom KPI dashboards',
      'Real-time analytics',
      'Performance metrics',
      'Business intelligence',
      'Data visualization',
      'Report generation'
    ]
  },
  {
    id: 'proposals',
    name: 'Proposals',
    tagline: 'Professional proposal creation and management',
    description: 'Create stunning proposals with multiple pricing options, track views, and convert more leads into customers with digital signatures.',
    category: 'Sales',
    price: 29.99,
    rating: 5.0,
    reviews: 0,
    icon: getIconComponent('proposals', 'h-8 w-8'),
    color: 'bg-blue-600', // Will be replaced by gradient
    type: 'addon',
    slug: 'proposals',
    uninstallable: true,
    features: [
      'Multi-offer proposals',
      'Digital signatures',
      'View tracking',
      'Custom branding',
      'Proposal templates',
      'Conversion analytics'
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory',
    tagline: 'Complete stock and inventory management',
    description: 'Manage your inventory, track stock levels, handle product sales, and monitor transactions with comprehensive reporting.',
    category: 'Operations',
    price: 39.99,
    rating: 5.0,
    reviews: 0,
    icon: <Package className="h-8 w-8" />,
    color: 'bg-green-600',
    type: 'addon',
    slug: 'inventory',
    uninstallable: true,
    features: [
      'Real-time stock tracking',
      'Product management',
      'Purchase orders',
      'Stock transactions',
      'Low stock alerts',
      'Inventory reports'
    ]
  },
  {
    id: 'products',
    name: 'Store',
    tagline: 'Sell products directly to customers',
    description: 'Create product sales, manage cart functionality, process payments, and send invoices with comprehensive product selling capabilities.',
    category: 'Sales',
    price: 24.99,
    rating: 5.0,
    reviews: 0,
    icon: <ShoppingCart className="h-8 w-8" />,
    color: 'bg-purple-600',
    type: 'addon',
    slug: 'products',
    uninstallable: true,
    features: [
      'Product catalog',
      'Shopping cart',
      'Payment processing',
      'Invoice generation',
      'Discount codes',
      'Customer management'
    ]
  },
  {
    id: 'gift-cards',
    name: 'Gift Cards',
    tagline: 'Create and manage virtual gift cards',
    description: 'Sell virtual gift cards to customers, track balances, manage redemptions, and boost revenue with prepaid gift card sales.',
    category: 'Sales',
    price: 4.99,
    rating: 5.0,
    reviews: 0,
    icon: <Gift className="h-8 w-8" />,
    color: 'bg-pink-600',
    type: 'addon',
    slug: 'gift-cards',
    uninstallable: true,
    features: [
      'Virtual gift cards',
      'Custom amounts',
      'Balance tracking',
      'Redemption management',
      'Email delivery',
      'Transaction history'
    ]
  },
  {
    id: 'payroll',
    name: 'Payroll',
    tagline: 'Employee payroll and time tracking management',
    description: 'Comprehensive payroll processing, time tracking, employee management, and automated payroll calculations with detailed reporting.',
    category: 'Finance',
    price: 49.99,
    rating: 5.0,
    reviews: 0,
    icon: <DollarSign className="h-8 w-8" />,
    color: 'bg-emerald-600',
    type: 'addon',
    slug: 'payroll',
    uninstallable: true,
    features: [
      'Payroll processing',
      'Time tracking integration',
      'Employee management',
      'Automated calculations',
      'Tax calculations',
      'Payroll reports'
    ]
  },
  {
    id: 'crm',
    name: 'CRM',
    tagline: 'Advanced customer relationship management',
    description: 'Comprehensive CRM system with lead tracking, customer insights, sales pipeline management, and advanced customer analytics.',
    category: 'Sales',
    price: 44.99,
    rating: 5.0,
    reviews: 0,
    icon: <Users className="h-8 w-8" />,
    color: 'bg-indigo-600',
    type: 'addon',
    slug: 'crm',
    uninstallable: true,
    features: [
      'Lead management',
      'Customer insights',
      'Sales pipeline',
      'Activity tracking',
      'Advanced analytics',
      'Customer segmentation'
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing',
    tagline: 'Comprehensive marketing and campaign management',
    description: 'Advanced marketing tools with campaign management, email marketing, customer outreach, and marketing analytics to grow your business.',
    category: 'Marketing',
    price: 34.99,
    rating: 5.0,
    reviews: 0,
    icon: <MessageSquare className="h-8 w-8" />,
    color: 'bg-pink-600',
    type: 'addon',
    slug: 'marketing',
    uninstallable: true,
    features: [
      'Campaign management',
      'Email marketing',
      'Customer outreach',
      'Marketing analytics',
      'Lead generation',
      'Marketing automation'
    ]
  },
  // Projects is discontinued: hidden from the store (migration 20260710040000
  // marks its add-on unavailable). Existing installs keep working.
  {
    id: 'expenses',
    name: 'Expenses',
    tagline: 'AI-powered expense tracking with automatic receipt categorization',
    description: 'Track and manage business expenses with AI-powered receipt scanning. Automatically categorize expenses, extract data from receipts, and generate expense reports.',
    category: 'Finance',
    price: 9.99,
    rating: 5.0,
    reviews: 0,
    icon: <Receipt className="h-8 w-8" />,
    color: 'bg-emerald-600',
    type: 'addon',
    slug: 'expenses',
    uninstallable: true,
    features: [
      'AI receipt scanning',
      'Automatic categorization',
      'Expense tracking',
      'Receipt storage',
      'Tax deduction tracking',
      'Expense reports'
    ]
  }
];

type StoreView = 'all' | 'integrations' | 'nexus-apps';

function initialStoreView(): StoreView {
  try {
    const params = new URLSearchParams(window.location.search);
    const platform = (params.get('platform') || '').toLowerCase().replace(/[_-]/g, '');
    if (
      params.get('tab') === 'integrations' ||
      params.get('twilio_connect') ||
      params.get('gbp_oauth') === '1' ||
      params.get('gbp_returning') === '1' ||
      platform === 'googlebusiness' ||
      params.get('pendingDataToken') ||
      params.get('pending_data_token')
    ) {
      return 'integrations';
    }
    if (params.get('tab') === 'nexus-apps') return 'nexus-apps';
  } catch { /* ignore */ }
  return 'all';
}

export default function AppStore() {
  const { selectedBusiness } = useBusiness();
  const { subscription } = useSubscription();
  const { coreApps: installedCoreApps, installedAddons, reload: reloadAppSystem } = useAppSystem();
  const [searchParams] = useSearchParams();
  const [storeView, setStoreView] = useState<StoreView>(initialStoreView);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);

  // Deep links such as /app-store?tab=integrations open that view.
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'integrations') setStoreView('integrations');
    else if (tab === 'nexus-apps') setStoreView('nexus-apps');
  }, [searchParams]);
  const [openUpdate, setOpenUpdate] = useState<AppUpdate | null>(null);
  const [installingApp, setInstallingApp] = useState<string | null>(null);
  const [uninstallApp, setUninstallApp] = useState<App | null>(null);
  const [currentPlanApps, setCurrentPlanApps] = useState<string[]>([]);
  const [addonPricing, setAddonPricing] = useState<Record<string, number>>({});
  
  // Create a set of installed app slugs for easy checking
  const installedAppSlugs = new Set([
    ...installedCoreApps.map(app => app.app?.slug).filter(Boolean),
    ...installedAddons.map(app => app.app?.slug).filter(Boolean)
  ]);

  // Fetch current plan's included apps
  React.useEffect(() => {
    const fetchCurrentPlanApps = async () => {
      if (!subscription?.active_plan_id) {
        setCurrentPlanApps([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('included_apps')
          .eq('plan_id', subscription.active_plan_id)
          .single();

        if (error) {
          console.error('Error fetching plan apps:', error);
          setCurrentPlanApps([]);
          return;
        }

        setCurrentPlanApps(data?.included_apps || []);
      } catch (err) {
        console.error('Error in fetchCurrentPlanApps:', err);
        setCurrentPlanApps([]);
      }
    };

    fetchCurrentPlanApps();
  }, [subscription?.active_plan_id]);

  // Load addon pricing from database
  React.useEffect(() => {
    const fetchAddonPricing = async () => {
      try {
        const { data: addonApps, error } = await supabase
          .from('addon_apps')
          .select('app_id, price_monthly')
          .eq('is_available', true);

        if (!error && addonApps) {
          const pricingMap: Record<string, number> = {};
          addonApps.forEach(addon => {
            pricingMap[addon.app_id] = addon.price_monthly;
          });
          setAddonPricing(pricingMap);
          console.log('📊 Loaded addon pricing:', pricingMap);
        }
      } catch (error) {
        console.error('Error fetching addon pricing:', error);
      }
    };

    fetchAddonPricing();
  }, []);

  // Get the correct price for an app (database pricing for addons, hardcoded for core)
  const getAppPrice = (app: App) => {
    if (app.type === 'addon') {
      // For addon apps, use database pricing
      const addonAppId = `${app.slug}-addon-app`;
      const dbPrice = addonPricing[addonAppId];
      
      console.log(`🔍 Getting price for ${app.name}:`, {
        appSlug: app.slug,
        addonAppId,
        dbPrice,
        fallbackPrice: app.price,
        addonPricingKeys: Object.keys(addonPricing)
      });
      
      return dbPrice || app.price;
    }
    // For core apps, use existing price
    return app.price;
  };

  // Add addon app to subscription
  const addAddonApp = async (appSlug: string) => {
    if (!selectedBusiness?.id) {
      console.error('❌ No business selected');
      toast.error('No business selected');
      return false;
    }

    try {
      const addonAppId = `${appSlug}-addon`;
      console.log(`🛒 Starting addon app purchase:`, {
        appSlug,
        addonAppId,
        businessId: selectedBusiness.id,
        businessName: selectedBusiness.name
      });

      const { data, error } = await supabase.functions.invoke('stripe-subscription', {
        body: {
          action: 'add-addon-app',
          businessId: selectedBusiness.id,
          addonAppId: addonAppId
        }
      });

      console.log('📦 Stripe function response:', { data, error });

      if (error) {
        console.error('❌ Error from stripe-subscription function:', error);
        console.error('❌ Error details:', JSON.stringify(error, null, 2));
        toast.error(`Failed to add addon: ${error.message || 'Unknown error'}`);
        return false;
      }

      if (!data) {
        console.error('❌ No data returned from function');
        toast.error('Failed to add addon: No response from server');
        return false;
      }

      if (!data.success) {
        console.error('❌ Function returned failure:', data);
        toast.error(`Failed to add addon: ${data.error || data.message || 'Unknown error'}`);
        return false;
      }

      console.log('✅ Addon app added successfully:', data);
      toast.success(data.message || 'Addon app added successfully!');
      
      // Reload app system to show the new app
      await reloadAppSystem();
      
      return true;
    } catch (error: any) {
      console.error('❌ Exception in addAddonApp:', error);
      console.error('❌ Error stack:', error?.stack);
      toast.error(`Failed to add addon: ${error?.message || 'Unknown error'}`);
      return false;
    }
  };

  // Remove addon app from subscription
  const removeAddonApp = async (appSlug: string) => {
    if (!selectedBusiness?.id) return false;

    try {
      const addonAppId = `${appSlug}-addon`;
      console.log(`🗑️ Removing addon app: ${addonAppId}`);

      const { data, error } = await supabase.functions.invoke('stripe-subscription', {
        body: {
          action: 'remove-addon-app',
          businessId: selectedBusiness.id,
          addonAppId: addonAppId
        }
      });

      if (error) {
        console.error('Error removing addon app:', error);
        toast.error('Failed to remove addon app');
        return false;
      }

      console.log('✅ Addon app removed successfully:', data);
      toast.success(data.message || 'Addon app removed successfully!');
      
      // Reload app system to hide the removed app
      await reloadAppSystem();
      
      return true;
    } catch (error) {
      console.error('Error removing addon app:', error);
      toast.error('Failed to remove addon app');
      return false;
    }
  };

  // Check if app is included in current subscription plan
  const isAppIncludedInPlan = (appSlug: string) => {
    if (!subscription?.active_plan_id) return false;
    
    // Convert app slug to app_id format
    const coreAppId = `${appSlug}-core-app`;
    const addonAppId = `${appSlug}-addon-app`;
    
    return currentPlanApps.includes(coreAppId) || currentPlanApps.includes(addonAppId);
  };

  // Debug: Log what we're getting from the app system
  console.log('🔍 Installed core apps:', installedCoreApps.map(app => ({ 
    name: app.app?.name, 
    slug: app.app?.slug,
    app_id: app.app_id 
  })));
  console.log('🔍 Installed app slugs set:', Array.from(installedAppSlugs));
  console.log('🔍 Current plan apps:', currentPlanApps);

  // Combine core apps, addon apps, and system pages
  const allApps = [...coreApps, ...addonApps];

  const filteredApps = allApps;

  // Remove featured and new apps sections since we're only showing core apps
  // const featuredApps: App[] = [];
  // const newApps: App[] = [];

  // Function to find the next available position (checks ALL positions 1-9)
  const findNextAvailablePosition = async () => {
    if (!selectedBusiness?.id) return 7; // Default to position 7

    try {
      // Get current positions directly from database to ensure fresh data
      // Don't filter by status - check ALL records to see what positions are taken
      const { data: currentApps, error } = await supabase
        .from('business_apps_v2')
        .select('app_id, visible_app_grid')
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('❌ Error fetching current apps:', error);
        return 7; // Default to position 7
      }

      console.log('🔍 All apps in database (checking positions):', currentApps);

      // Get all occupied positions in visible grid (filter out null/undefined)
      const occupiedPositions = new Set(
        currentApps
          ?.map(app => app.visible_app_grid)
          .filter(pos => pos !== null && pos !== undefined) || []
      );

      console.log('🔍 Occupied positions:', Array.from(occupiedPositions).sort((a, b) => a - b));

      // Check all positions from 1-9 and find the first available one
      for (let position = 1; position <= 9; position++) {
        if (!occupiedPositions.has(position)) {
          console.log('✅ Found available position:', position);
          return position;
        }
      }

      // If all positions 1-9 are occupied, start from position 10
      let position = 10;
      while (occupiedPositions.has(position)) {
        position++;
      }
      
      console.log('⚠️ Positions 1-9 full, using position:', position);
      return position;
    } catch (error) {
      console.error('❌ Error in findNextAvailablePosition:', error);
      return 7; // Default fallback
    }
  };

  const handleInstall = async (appId: string) => {
    if (!selectedBusiness?.id) return;

    const app = allApps.find(a => a.id === appId);
    if (!app || !app.slug) {
      toast.error('App not found');
      return;
    }

    // Check if app is included in current subscription plan
    if (!isAppIncludedInPlan(app.slug)) {
      // App is not included - try to purchase as addon
      console.log(`🛒 App not included in plan, attempting to purchase as addon: ${app.name}`);
      
      setInstallingApp(appId);
      const success = await addAddonApp(app.slug);
      setInstallingApp(null);
      
      if (success) {
        // Show success message and refresh page to update UI
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      return;
    }

    setInstallingApp(appId);
    
    // Add a realistic delay to make the installation feel more substantial
    await new Promise(resolve => setTimeout(resolve, 2500));
    
    try {
      const app = allApps.find(a => a.id === appId);
      if (!app || !app.slug) {
        toast.error('App not found');
        setInstallingApp(null);
        return;
      }

      console.log('🔄 Installing app:', app.name, 'slug:', app.slug, 'type:', app.type);

      // Determine app ID format and find available position
      let databaseAppId: string;
      
      if (app.type === 'core') {
        databaseAppId = `${app.slug}-core-app`;
      } else {
        databaseAppId = `${app.slug}-addon-app`;
      }
      
      // Find next available position (checks all existing apps)
      const gridPosition = await findNextAvailablePosition();
      
      console.log('🔍 Database App ID format:', databaseAppId);
      console.log('📍 Installing app at position:', gridPosition);
      
      // Check if app already exists (shouldn't happen after deletion, but just in case)
      const { data: existingApp } = await supabase
        .from('business_apps_v2')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('app_id', databaseAppId)
        .maybeSingle();

      if (existingApp) {
        // App exists - this shouldn't happen if we're deleting properly on cancel
        // Just update the position to be safe
        console.log('⚠️ App already exists in database, updating position:', gridPosition);
        const { data: updateData, error } = await supabase
          .from('business_apps_v2')
          .update({ 
            status: 'active',
            visible_app_grid: gridPosition,
            hidden_app_grid: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingApp.id)
          .select();

        console.log('📝 Update result:', { updateData, error });
        if (error) throw error;
      } else {
        // Create new app installation (normal flow)
        console.log('🆕 Creating new app installation:', {
          business_id: selectedBusiness.id,
          app_id: databaseAppId,
          status: 'active',
          visible_app_grid: gridPosition
        });
        
        const { data: insertData, error } = await supabase
          .from('business_apps_v2')
          .insert({
            business_id: selectedBusiness.id,
            app_id: databaseAppId,
            status: 'active',
            visible_app_grid: gridPosition,
            hidden_app_grid: null
          })
          .select();

        console.log('📝 Insert result:', { insertData, error });
        if (error) {
          console.error('❌ Insert error details:', error);
          throw error;
        }
      }

      // Small delay to ensure database changes are committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload the app system
      console.log('🔄 Reloading app system after installation...');
      await reloadAppSystem();
      console.log('✅ App system reloaded successfully');
      
      toast.success(`${app.name} has been installed successfully!`);

    } catch (error) {
      console.error('❌ Error installing app:', error);
      toast.error('Failed to install app');
    } finally {
      setInstallingApp(null);
    }
  };

  const handleUninstall = async (app: App) => {
    if (!selectedBusiness?.id || !app.slug) return;

    try {
      console.log('🔄 Starting uninstall for:', app.name, 'slug:', app.slug, 'business:', selectedBusiness.id);
      
      // First, let's check what's in the database
      const { data: existingApps } = await supabase
        .from('business_apps_v2')
        .select('*')
        .eq('business_id', selectedBusiness.id);
      
      console.log('📋 Current business apps:', existingApps);
      
      // Find the specific app record - need to match against the correct field
      // The database has app_id like "mobile-calendar-core-app" or "proposals-addon-app"
      const appRecord = existingApps?.find(record => 
        record.app_id === `${app.slug}-core-app` || 
        record.app_id === `${app.slug}-addon-app` || 
        record.app_id === app.slug
      );
      console.log('🎯 Found app record:', appRecord);
      
      if (!appRecord) {
        console.error('❌ App record not found for slug:', app.slug);
        console.error('❌ Available app_ids:', existingApps?.map(r => r.app_id));
        toast.error('App not found in database');
        return;
      }
      
      // Delete the app record completely
      console.log('🗑️ Deleting app from business_apps_v2');
      const { error: deleteError } = await supabase
        .from('business_apps_v2')
        .delete()
        .eq('business_id', selectedBusiness.id)
        .eq('app_id', appRecord.app_id);

      if (deleteError) {
        console.error('❌ Error deleting app:', deleteError);
        toast.error('Failed to uninstall app');
        return;
      }

      console.log('✅ App deleted successfully, reloading app system...');
      
      // Small delay to ensure database changes are committed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reload the app system to update the navigation
      await reloadAppSystem();
      
      toast.success(`${app.name} has been uninstalled successfully!`);
      setUninstallApp(null);
    } catch (error) {
      console.error('❌ Error uninstalling app:', error);
      toast.error('Failed to uninstall app');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-nexus-connect-page>
      {/* Header - Mobile Optimized */}
      <div className="bg-white border-b border-gray-100">
        <div className="px-4 md:px-6 pt-3">
          {/* Top Row: Title, My Subscription */}
          <div className="flex items-center justify-between h-12 md:h-14">
            <div className="flex items-center gap-2 md:gap-4">
              <h1 className="text-lg md:text-xl font-semibold text-gray-900">Nexus Connect</h1>
              
              {/* My Subscription Button */}
              <Link
                to="/my-apps"
                className="px-2 py-1 md:px-3 md:py-1.5 bg-blue-500 text-white rounded-lg text-xs md:text-sm font-medium hover:bg-blue-600 transition-colors"
              >
                My Subscription
              </Link>
            </div>
          </div>
          <div className="flex gap-1 p-1 mb-3 bg-gray-100 rounded-xl w-full sm:w-auto sm:inline-flex" role="tablist" aria-label="Nexus Connect view">
            {([
              ['all', 'All'],
              ['integrations', 'Integrations'],
              ['nexus-apps', 'Nexus Apps'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={storeView === value}
                onClick={() => setStoreView(value)}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  storeView === value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-8">
        {storeView === 'all' && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">What's new</h2>
              <span className="text-sm text-gray-500">{APP_UPDATES.length} update{APP_UPDATES.length === 1 ? '' : 's'}</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {APP_UPDATES.map((update) => (
                <div key={update.id} className="shrink-0">
                  <p className="mb-1.5 text-[11px] text-gray-500">{update.released === false ? 'Coming soon' : formatAppUpdateDate(update.releasedOn)}</p>
                  <AppUpdatePost update={update} compact titleId={`whats-new-${update.id}`} onOpen={() => setOpenUpdate(update)} />
                </div>
              ))}
            </div>
            {openUpdate && (
              <AppUpdateModal replay={openUpdate} onClose={() => setOpenUpdate(null)} />
            )}
          </section>
        )}

        {(storeView === 'all' || storeView === 'integrations') && (
        <section className={storeView === 'all' ? 'mb-12' : ''}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Integrations</h2>
            <span className="text-sm text-gray-500">{import.meta.env.DEV ? 5 : 4} connections</span>
          </div>
          <Integrations variant="nexusConnect" />
        </section>
        )}

        {(storeView === 'all' || storeView === 'nexus-apps') && (
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Nexus Apps</h2>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">{filteredApps.length} apps</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredApps.map(app => (
                <div
                  key={app.id}
                  className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all duration-200"
                >
                <div className="flex items-start space-x-3 mb-3">
                  {customAppIcons[app.slug || app.id] ? (
                    <div className="w-11 h-11 rounded-xl shadow-sm flex-shrink-0 overflow-hidden bg-white">
                      <img src={customAppIcons[app.slug || app.id]} alt={app.name} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' }} />
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-blue-600 to-blue-500 text-white p-2.5 rounded-xl shadow-sm flex-shrink-0">
                      {React.cloneElement(app.icon, { className: 'h-6 w-6' })}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      {app.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">{app.category}</p>
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">{app.tagline}</p>
                
                <div className="flex items-center justify-between">
                  {installedAppSlugs.has(app.slug || app.id) ? (
                    app.uninstallable ? (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setUninstallApp(app);
                        }}
                        className="text-xs text-green-600 font-medium flex items-center hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded-lg transition-colors"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Installed
                      </button>
                    ) : (
                      <span className="text-xs text-green-600 font-medium flex items-center px-2 py-1">
                        <Check className="h-3 w-3 mr-1" />
                        Installed
                      </span>
                    )
                  ) : (
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-900">
                          {getAppPrice(app) === 'free' ? 'Free' : `$${getAppPrice(app)}`}
                        </span>
                        {/* Plan inclusion indicator */}
                        {isAppIncludedInPlan(app.slug || app.id) ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            Included
                          </span>
                        ) : (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                            Add-on
                          </span>
                        )}
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInstall(app.id);
                        }}
                        disabled={installingApp === app.id}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors disabled:opacity-50 flex items-center ${
                          isAppIncludedInPlan(app.slug || app.id) 
                            ? 'bg-blue-500 text-white hover:bg-blue-600' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {installingApp === app.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                            {isAppIncludedInPlan(app.slug || app.id) ? 'Installing' : 'Purchasing'}
                          </>
                        ) : isAppIncludedInPlan(app.slug || app.id) ? (
                          'Download'
                        ) : (
                          'Purchase'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
        )}
      </div>

      {/* Apple-style App Detail Modal - Disabled for now */}
      {false && selectedApp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`${selectedApp.color} text-white p-4 rounded-3xl shadow-lg`}>
                    {React.cloneElement(selectedApp.icon, { className: 'h-10 w-10' })}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{selectedApp.name}</h2>
                    <p className="text-base text-gray-500">{selectedApp.category}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => setSelectedApp(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="p-8">
                {/* App Info */}
                <div className="mb-8">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">{selectedApp.description}</p>
                  
                  <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl">
                    <div>
                      <p className="text-3xl font-bold text-gray-900 mb-1">
                        {selectedApp.price === 'free' ? 'Free' : `$${selectedApp.price}`}
                      </p>
                      {selectedApp.price !== 'free' && (
                        <p className="text-sm text-gray-500">per month</p>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {/* Plan inclusion indicator in modal */}
                      <div className="flex items-center justify-center">
                        {isAppIncludedInPlan(selectedApp.slug || selectedApp.id) ? (
                          <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium flex items-center">
                            <Check className="h-4 w-4 mr-1" />
                            Included in your plan
                          </span>
                        ) : (
                          <span className="text-sm bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-medium">
                            Requires upgrade or add-on purchase
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          if (installedAppSlugs.has(selectedApp.slug || selectedApp.id)) {
                            if (selectedApp.uninstallable) {
                              setUninstallApp(selectedApp);
                            }
                          } else {
                            handleInstall(selectedApp.id);
                          }
                        }}
                        disabled={
                          installingApp === selectedApp.id || 
                          (installedAppSlugs.has(selectedApp.slug || selectedApp.id) && !selectedApp.uninstallable) ||
                          (!installedAppSlugs.has(selectedApp.slug || selectedApp.id) && !isAppIncludedInPlan(selectedApp.slug || selectedApp.id))
                        }
                        className={`w-full px-8 py-4 rounded-2xl font-semibold text-base transition-all shadow-sm ${
                          installedAppSlugs.has(selectedApp.slug || selectedApp.id)
                            ? selectedApp.uninstallable
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : installingApp === selectedApp.id
                            ? 'bg-blue-100 text-blue-600'
                            : isAppIncludedInPlan(selectedApp.slug || selectedApp.id)
                            ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {installedAppSlugs.has(selectedApp.slug || selectedApp.id) ? (
                          <span className="flex items-center justify-center">
                            <Check className="h-5 w-5 mr-2" />
                            {selectedApp.uninstallable ? 'Installed' : 'Required'}
                          </span>
                        ) : installingApp === selectedApp.id ? (
                          <span className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                            Installing
                          </span>
                        ) : isAppIncludedInPlan(selectedApp.slug || selectedApp.id) ? (
                          <span className="flex items-center justify-center">
                            <Download className="h-5 w-5 mr-2" />
                            Download
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <DollarSign className="h-5 w-5 mr-2" />
                            Purchase
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 tracking-tight">What's Included</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedApp.features.map((feature, index) => (
                      <div key={index} className="flex items-center p-4 bg-gray-50 rounded-xl">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Screenshots */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 tracking-tight">Preview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl aspect-[4/3] flex items-center justify-center shadow-inner">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-300 rounded-xl mx-auto mb-3 flex items-center justify-center">
                            <div className={`${selectedApp.color} text-white p-2 rounded-lg`}>
                              {React.cloneElement(selectedApp.icon, { className: 'h-6 w-6' })}
                            </div>
                          </div>
                          <span className="text-gray-500 text-sm font-medium">Screenshot {i}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apple-style Uninstall Modal */}
      {uninstallApp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">Remove {uninstallApp.name}?</h3>
            </div>
            
            {/* Content */}
            <div className="p-8">
              <div className="flex items-start space-x-4">
                {customAppIcons[uninstallApp.slug || uninstallApp.id] ? (
                  <div className="w-14 h-14 rounded-2xl flex-shrink-0 shadow-sm overflow-hidden bg-white">
                    <img src={customAppIcons[uninstallApp.slug || uninstallApp.id]} alt={uninstallApp.name} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' }} />
                  </div>
                ) : (
                  <div className={`${uninstallApp.color} text-white p-4 rounded-2xl flex-shrink-0 shadow-sm`}>
                    {React.cloneElement(uninstallApp.icon, { className: 'h-8 w-8' })}
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Removing <strong className="text-gray-900">{uninstallApp.name}</strong> will remove it from your navigation and you won't be able to access its features.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <p className="text-sm text-amber-800 leading-relaxed">
                      <strong>Note:</strong> You can reinstall this app anytime from Nexus Connect.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex gap-3 p-6 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setUninstallApp(null)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-white transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUninstall(uninstallApp)}
                className="flex-1 px-6 py-3 bg-red-500 text-white rounded-2xl hover:bg-red-600 transition-colors font-medium shadow-sm"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
